import React from 'react';
import './MeterMeta.less';
import classNames from 'classnames';
import LabeledList from '../LabeledList/LabeledList';
import OptionalLabel from '../OptionalLabel/OptionalLabel';
import { splitJoin } from '../../../helper/TextHelper';
import { Meter } from '../../../redux/jobs/jobs.types';
import { useTranslation } from 'react-i18next';
import { resolveMeterType } from '../../../helper/MeterHelper';
import { hasVisibleField } from '../../../fieldVisibility/fieldVisibility';
import { useFieldVisibility } from '../../../fieldVisibility/useFieldVisibility';

interface IMeterMetaProps {
    meter: Meter;
    className?: string;
}

const MeterMeta = ({ meter, className }: IMeterMetaProps) => {
    const { t } = useTranslation();
    const visibility = useFieldVisibility();

    if (!meter) return null;

    // Bleibt keine Zeile übrig, ist der Block leer — dann gar nichts rendern,
    // wie schon heute bei fehlendem meter.
    const hasAnything = hasVisibleField(visibility, [
        'meter.meterNumber',
        'meter.meterType',
        'meter.meterAddress.full',
        'meter.meterAddress.objectKey',
        'meter.meterAddress.locationDescription',
        'meter.meterAddress.hint',
    ]);
    if (!hasAnything) return null;

    return (
        <div className={classNames('meter-meta', className)}>
            <LabeledList>
                <OptionalLabel field={'meter.meterNumber'} label={t('jobs.details.labels.meterNumber')}>
                    {meter.meterNumber}
                </OptionalLabel>

                <OptionalLabel field={'meter.meterType'} label={t('jobs.details.labels.meterType')}>
                    {meter.customMeterType || t(`common.metertypes.${resolveMeterType(meter)}`)}
                </OptionalLabel>

                <OptionalLabel field={'meter.meterAddress.full'} label={t('evaluation.details.labels.meterAddress')}>
                    {/* Der hasVisibleField-Vorbau ist nötig, weil die drei <div>-Blöcke
                        immer wahrheitswertige Kinder sind: OptionalLabel würde die Zeile
                        sonst rendern, obwohl darin nur ein leeres <div> steht. pick()
                        leert nur den INHALT. Bei einer komplett versteckten Anschrift
                        greift das field am OptionalLabel selbst — die Kaskade nimmt
                        'full' und alle Bestandteile. */}
                    {hasVisibleField(visibility, [
                        'meter.meterAddress.firstname',
                        'meter.meterAddress.lastname',
                    ]) &&
                        (meter.meterAddress?.firstname || meter.meterAddress?.lastname) && (
                            <div>
                                {splitJoin(
                                    ' ',
                                    visibility.pick('meter.meterAddress.firstname', meter.meterAddress?.firstname),
                                    visibility.pick('meter.meterAddress.lastname', meter.meterAddress?.lastname),
                                )}
                            </div>
                        )}
                    {(meter.meterAddress?.street || meter.meterAddress?.houseNumber) && (
                        <div>
                            {splitJoin(
                                ' ',
                                visibility.pick('meter.meterAddress.street', meter.meterAddress?.street),
                                visibility.pick('meter.meterAddress.houseNumber', meter.meterAddress?.houseNumber),
                            )}
                        </div>
                    )}
                    {(meter.meterAddress?.zip || meter.meterAddress?.city) && (
                        <div>
                            {splitJoin(
                                ' ',
                                visibility.pick('meter.meterAddress.zip', meter.meterAddress?.zip),
                                splitJoin(
                                    ', ',
                                    visibility.pick('meter.meterAddress.city', meter.meterAddress?.city),
                                    visibility.pick('meter.meterAddress.cityDistrict', meter.meterAddress?.cityDistrict),
                                ),
                            )}
                        </div>
                    )}
                </OptionalLabel>

                <OptionalLabel field={'meter.meterAddress.objectKey'} label={t('jobs.details.labels.objectKey')}>
                    {meter.meterAddress?.objectKey}
                </OptionalLabel>

                <OptionalLabel
                    field={'meter.meterAddress.locationDescription'}
                    label={t('jobs.details.labels.locationDescription')}
                >
                    {meter.meterAddress?.locationDescription}
                </OptionalLabel>

                <OptionalLabel field={'meter.meterAddress.hint'} label={t('jobs.details.labels.hint')}>
                    {meter.meterAddress?.hint}
                </OptionalLabel>
            </LabeledList>
        </div>
    );
};

export default MeterMeta;
